import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useStore, ScheduleData, Draft, ApiError, useScheduleMutation } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { LayoutHeader, NavigationDelete, ScheduleForm, ScheduleHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const ScheduleEdit = () => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const store = useStore()
  const { message } = store
  const [confirmDelete, setConfirmDelete] = useState<Draft<ScheduleData>>()
  const [{ companyId, scheduleId }] = useNumberParams()
  const { t } = useTranslation()
  const mutation = useScheduleMutation({ companyId })

  const save = async (draft: Draft<ScheduleData>) => {
    try {
      setErrors({})
      await mutation.update(draft)
      message.add({
        message: t('Schedule was updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`SCHEDULE: Schedule failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Schedule could not be updated')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }
    router.push(`/companies/${companyId}/workspaces/schedules`)
  }

  const remove = async () => {
    try {
      await mutation.delete(confirmDelete!)
      message.add({
        message: t('Schedule was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`SCHEDULE: Schedule deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Schedule could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    router.push(`/companies/${companyId}/workspaces/schedules`)
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <ScheduleHeader companyId={companyId} scheduleId={scheduleId} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<ScheduleForm.Skeleton />}>
          <ScheduleForm
            companyId={companyId}
            scheduleId={scheduleId}
            onSave={save}
            onDelete={async (draft) => setConfirmDelete(draft)}
            errors={errors}
          />
        </Suspense>
        <NavigationDelete
          entity={t('Schedule')}
          visible={!!confirmDelete}
          onClose={() => setConfirmDelete(undefined)}
          onDelete={remove}
        />
      </View>
    </View>
  )
}

export default ScheduleEdit
